import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { useEvent } from "shared/lib/useEvent"
import { useFormSubmit } from "shared/lib/useFormSubmit"
import { ApiResponse } from "shared/types/ApiResponse"

import { useProductObjLoad } from "entities/products/model/useProductObjLoad"
import {
  ProductConfiguratorResult,
  useProductConfiguratorModel
} from "widgets/product-configurator/model/useProductConfiguratorModel"

import { ProductConfigurationOverlayConnectorProps } from "../ProductConfigurationOverlayConnector"

export interface ProductConfigurationOverlayModel {
  title: string | undefined
  productType: string
  productConfiguratorModel: ReturnType<typeof useProductConfiguratorModel>
  isLoading?: boolean
  closeOverlay: () => void
}

export function useProductConfigurationOverlayModel({
  payload
}: ProductConfigurationOverlayConnectorProps): ProductConfigurationOverlayModel {
  const { closeOverlay } = useCurrentOverlay()
  const { title, productType } = useProductObjLoad(
    payload.configuration.variantKey
  )

  const submitConfiguration = useEvent(
    async (
      configuration: ProductConfiguratorResult
    ): Promise<ApiResponse<unknown>> => {
      const result = await payload.onSubmit?.(configuration)
      return result ?? { data: {} } // void → treated as success
    }
  )

  const { handleSubmit, isSubmitting } = useFormSubmit<
    ProductConfiguratorResult & Record<string, unknown>
  >({
    onSubmit: submitConfiguration as (
      data: ProductConfiguratorResult & Record<string, unknown>
    ) => Promise<ApiResponse<unknown>>,
    onSuccess: closeOverlay
  })

  const productConfiguratorModel = useProductConfiguratorModel({
    ...payload.configuration,
    mode: payload.mode,
    onSubmit: handleSubmit as (
      configuration: ProductConfiguratorResult
    ) => void,
    isSubmitting
  })

  return {
    title,
    productType,
    productConfiguratorModel,
    isLoading: isSubmitting,
    closeOverlay
  }
}
